/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Página: Workforce » Operadores (resiliente a shapes e refresh)
 * - Fonte autoritativa: /workforce/operators/ (linha Operator; ADR-0036 D3).
 * - Estado (online/em-pausa/em-tarefa) lido DIRETO do operator row (ADR-0012 F1),
 *   sem sub-fetches por linha.
 * - Evita esvaziar a lista se um refresh pontual falhar na VPS.
 * ========================================================================== */

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, normalizePath } from '@/lib/api'

/* Tipos ------------------------------------------------------------------- */
type User = {
  id: number
  username: string
  email?: string
  first_name?: string
  last_name?: string
  is_active?: boolean
  is_staff?: boolean
  last_login?: string | null
  role?: string
  operator_id?: number // ADR-0036 F2: Operator.id (link skills); id = User.id
  // ADR-0012 F1: estado projetado pelo backend (snake_case do JSON).
  online?: boolean
  available?: boolean
  current_interval?: string | null
  current_task?: { id: number; kind: string; status: string } | null
}

type Task = {
  id: number
  kind?: string
  status?: string
  title?: string
  updated_at?: string
  warehouse_name?: string
  site_name?: string
  current_location_code?: string
  assignee?: number
  payload?: Record<string, any>
}

type OperatorRow = {
  user: User
  online: boolean
  available: boolean
  currentInterval?: string | null // camel interno; vem de current_interval (snake) do backend
  task?: Task | null
}

/* Helpers ----------------------------------------------------------------- */
const DEBUG = false // deixe true se quiser ver os logs no console
const log = (...a: any[]) => {
  if (DEBUG) console.debug('[WF]', ...a)
}

function pickResults(data: any): any[] {
  if (!data) return []
  if (Array.isArray(data)) return data
  if (Array.isArray(data.results)) return data.results
  if (Array.isArray(data.items)) return data.items
  return []
}

/* ADR-0036 D3: a fonte de "operador" e' a linha Operator (/workforce/operators/),
   nao User-por-role. Mapeia a linha -> User: id = User.id (sub-fetches de
   tarefa/sessao); operator_id = Operator.id (link de skills). */
function operatorToUser(item: any): User | null {
  if (!item || item.user_id == null || item.id == null) return null
  return {
    id: item.user_id,
    operator_id: item.id,
    username: item.username ?? '',
    email: item.email ?? undefined,
    // ADR-0012 F1: estado projetado (mantem o snake do JSON no User).
    online: !!item.online,
    available: !!item.available,
    current_interval: item.current_interval ?? null,
    current_task: item.current_task ?? null,
  }
}

function toName(u: User): string {
  const n = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim()
  return n || u.username || `#${u.id}`
}

/* HTTP -------------------------------------------------------------------- */
async function getJSON<T = any>(path: string): Promise<T> {
  const p = normalizePath(path) // garante base e slash/?
  const { data } = await api.get(p)
  return data as T
}

/** ADR-0036 D3: fonte autoritativa UNICA -- a linha Operator. Devolve {source, users, reports}. */
async function probeCandidates() {
  const src = 'workforce/operators/?page_size=500'
  const reports: Array<{
    source: string
    raw: number
    asUsers: number
    filtered: number
  }> = []
  try {
    const data = await getJSON<any>(src)
    const rawArr = pickResults(data)
    const users = rawArr
      .map(operatorToUser)
      .filter((u): u is User => u !== null)
    reports.push({
      source: src,
      raw: rawArr.length,
      asUsers: users.length,
      filtered: users.length,
    })
    log(`probe ${src}`, reports[0])
    return { source: src, users, reports }
  } catch (e: any) {
    log(`probe ${src} -> erro`, e?.message || e)
    return { source: null as string | null, users: [] as User[], reports }
  }
}

/* Página ------------------------------------------------------------------ */
export default function OperatorSessions() {
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState<OperatorRow[]>([])
  const [query, setQuery] = useState('')
  const [auto, setAuto] = useState(true)
  const timer = useRef<number | null>(null)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) => {
      const u = r.user
      const name = toName(u).toLowerCase()
      const email = (u.email || '').toLowerCase()
      return (
        name.includes(q) ||
        (u.username || '').toLowerCase().includes(q) ||
        email.includes(q)
      )
    })
  }, [rows, query])

  /** Se o refresh vier vazio mas já tínhamos dados, não limpa a lista. */
  async function load({ allowClear = false }: { allowClear?: boolean } = {}) {
    setLoading(true)
    try {
      const { source, users, reports } = await probeCandidates()

      if (!users.length) {
        log('nenhuma fonte com Operators. reports:', reports)
        if (!allowClear && rows.length) {
          log('mantendo linhas atuais (guard contra esvaziamento acidental)')
          return
        }
        setRows([])
        return
      }

      // ADR-0012 F1: estado lido DIRETO do operator row (online/available/em-pausa/
      // em-tarefa) -- sem sub-fetches por linha. Mapeamento sincrono.
      const out: OperatorRow[] = users.map((u) => {
        const ct = u.current_task
        const task: Task | null = ct
          ? { id: ct.id, kind: ct.kind, status: ct.status }
          : null
        return {
          user: u,
          online: !!u.online,
          available: !!u.available,
          currentInterval: u.current_interval ?? null,
          task,
        } as OperatorRow
      })

      log('fonte escolhida:', source, 'linhas:', out.length)
      setRows(out)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load({ allowClear: true })
  }, [])

  useEffect(() => {
    if (!auto) {
      if (timer.current) window.clearInterval(timer.current)
      timer.current = null
      return
    }
    timer.current = window.setInterval(
      () => load({ allowClear: false }),
      10_000,
    ) as unknown as number
    return () => {
      if (timer.current) window.clearInterval(timer.current)
    }
  }, [auto])

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Título + Search (panel com contraste dark) */}
      <div className="panel rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 p-3">
        <div className="mb-3">
          <h1 className="text-xl font-semibold">Operadores</h1>
          <p className="text-sm text-neutral-600 dark:text-neutral-300">
            Tela de acompanhamento de status dos operadores (online, tarefa
            ativa e última atividade).
          </p>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-2">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nome, usuário ou e-mail..."
              className="input w-72 max-w-full"
              aria-label="Buscar operadores"
            />
            <button
              className="btn"
              onClick={() => load({ allowClear: false })}
              disabled={loading}
            >
              {loading ? 'Atualizando...' : 'Atualizar'}
            </button>
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={auto}
              onChange={(e) => setAuto(e.target.checked)}
            />
            <span className="text-sm">Auto-refresh</span>
          </label>
        </div>
      </div>

      {/* Tabela (panel com fundo dark e texto branco em .dark) */}
      <div className="panel overflow-auto rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950">
        <table className="min-w-full table-auto text-sm text-neutral-900 dark:text-neutral-100">
          <thead className="bg-neutral-50 dark:bg-neutral-900 text-neutral-700 dark:text-neutral-100">
            <tr>
              <th className="text-left px-3 py-2 w-[220px]">Operador</th>
              <th className="text-left px-3 py-2">Online</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2 w-[160px]">Ações</th>
            </tr>
          </thead>

          <tbody>
            {filtered.map((r) => {
              const u = r.user
              const name = toName(u)
              // ADR-0012 F1: 3 estados (Offline / Em pausa (intervalo) / Online).
              const presence = !r.online
                ? 'Offline'
                : r.available
                  ? 'Online'
                  : `Em pausa${r.currentInterval ? ` (${r.currentInterval})` : ''}`
              const onlineDot = !r.online
                ? 'bg-neutral-600'
                : r.available
                  ? 'bg-green-500'
                  : 'bg-amber-500'
              const status = r.task?.id
                ? `Em ${r.task.kind || 'tarefa'} (#${r.task.id})`
                : 'Stand-by'
              return (
                <tr
                  key={u.id}
                  className="border-t border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 hover:bg-neutral-50 dark:hover:bg-neutral-900/60"
                >
                  <td className="px-3 py-2">
                    <div className="font-medium">{name}</div>
                    <div className="text-xs text-neutral-500 dark:text-neutral-300">
                      @{u.username} {u.email ? `• ${u.email}` : ''}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block h-2.5 w-2.5 rounded-full align-middle ${onlineDot}`}
                    />{' '}
                    <span className="align-middle">{presence}</span>
                  </td>
                  <td className="px-3 py-2">{status}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-2">
                      <Link
                        className="btn btn-xs border border-neutral-300 dark:border-neutral-700 bg-transparent text-neutral-700 dark:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                        to={`/workforce/operators/${u.operator_id}/skills`}
                      >
                        Skills
                      </Link>
                      <Link
                        className="btn btn-xs border border-neutral-300 dark:border-neutral-700 bg-transparent text-neutral-700 dark:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                        to={`/workforce/tasks?assignee=${u.id}`}
                      >
                        Tarefas
                      </Link>
                      <Link
                        className="btn btn-xs border border-neutral-300 dark:border-neutral-700 bg-transparent text-neutral-700 dark:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                        to={`/workforce/operators/${u.operator_id}/performance`}
                      >
                        Desempenho
                      </Link>
                    </div>
                  </td>
                </tr>
              )
            })}

            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  className="px-3 py-10 text-center text-neutral-500 dark:text-neutral-300"
                >
                  Nenhum operador encontrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
